import { HOW_IT_WORKS_STEPS, HERO_IMAGE_SIMPLE } from "../data/mock";
import { HERO_IMAGE_DESK } from "../data/mock";
import { Upload, Search, CheckSquare, Sparkles, PenTool, Download, ArrowRight } from "lucide-react";

const iconMap = { Upload, Search, CheckSquare, Sparkles, PenTool, Download };

export const HowItWorksSection = ({ onOrderClick, onInfoClick }) => {
  return (
    <section id="how-it-works" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#F4F7FC] to-white" />
      <div className="absolute top-20 right-0 w-80 h-80 bg-[#FFC72C]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-[#0049B3]/5 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 bg-[#FFC72C]/10 border border-[#FFC72C]/20 rounded-full px-4 py-2 mb-6">
            <span className="text-sm font-semibold text-[#B8860B]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Jednoduchý proces
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F1B3D]"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: '1.35' }}
          >
            Jak to funguje?
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFC72C] to-[#E5A800]"> 6 kroků</span> k novému ŠVP
          </h2>
          <p className="mt-6 text-lg text-[#4A5B7A] leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Od nahrání dokumentu po export hotového ŠVP pro mateřskou školu — celý proces zvládnete za pár hodin.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection line (desktop) */}
          <div className="hidden lg:block absolute top-24 left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-[#0049B3]/10 via-[#0049B3]/20 to-[#0049B3]/10" />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {HOW_IT_WORKS_STEPS.map((step, index) => {
              const Icon = iconMap[step.icon];
              return (
                <div
                  key={step.step}
                  className="group relative"
                >
                  <div className="bg-white rounded-3xl p-8 border border-[#E8EDF5] hover:border-[#0049B3]/20 hover:shadow-2xl hover:shadow-blue-900/8 transition-all duration-500 hover:-translate-y-2 h-full">
                    {/* Step number & icon */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="relative">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0049B3] to-[#003380] flex items-center justify-center shadow-lg shadow-blue-600/20 group-hover:shadow-blue-600/40 transition-shadow duration-500">
                          <span
                            className="text-[#FFC72C] text-xl font-extrabold"
                            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                          >
                            {step.step}
                          </span>
                        </div>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-[#0049B3]/5 flex items-center justify-center">
                        {Icon && <Icon className="w-5 h-5 text-[#0049B3]" />}
                      </div>
                    </div>

                    {/* Content */}
                    <h3
                      className="text-xl font-bold text-[#0F1B3D] mb-3"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-sm text-[#4A5B7A] leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {step.description}
                    </p>

                    {/* Details */}
                    <div className="space-y-2">
                      {step.details.map((detail) => (
                        <div key={detail} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#FFC72C]" />
                          <span className="text-xs font-medium text-[#3D4F6F]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {detail}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Arrow between cards (desktop, not last in row) */}
                  {index < 5 && index % 3 !== 2 && (
                    <div className="hidden lg:flex absolute top-24 -right-4 z-10 w-8 h-8 rounded-full bg-white border border-[#E8EDF5] items-center justify-center shadow-sm">
                      <ArrowRight className="w-4 h-4 text-[#0049B3]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA with hero */}
        <div className="mt-20 relative bg-gradient-to-br from-[#FFF9EB] to-[#F8FAFF] rounded-3xl p-8 lg:p-12 border border-[#FFC72C]/15 overflow-hidden">
          <div className="absolute -right-8 -bottom-8 opacity-20 lg:opacity-25 hidden sm:block">
            <img src={HERO_IMAGE_DESK} alt="" className="w-44 lg:w-52 hero-image-blend" style={{ mixBlendMode: 'multiply' }} />
          </div>
          <div className="relative max-w-2xl">
            <h3
              className="text-2xl lg:text-3xl font-bold text-[#0F1B3D] mb-4"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Připraveni začít?
            </h3>
            <p className="text-[#4A5B7A] mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              Celý proces od nahrání po export zvládnete za pár hodin. Objednejte si svého Agenta ŠVP.
            </p>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E5B326] text-[#1A1A2E] font-bold px-8 py-4 rounded-2xl shadow-lg shadow-amber-400/25 hover:shadow-amber-400/40 hover:-translate-y-0.5 transition-all duration-300" onClick={onOrderClick}>
                  Chci svého Agenta ŠVP<ArrowRight className="w-5 h-5" />
                </button>
                <button className="inline-flex items-center gap-2 border-2 border-[#0049B3]/15 text-[#0049B3] hover:bg-[#0049B3]/5 font-semibold px-8 py-4 rounded-2xl transition-all duration-300" onClick={onInfoClick}>
                  Chci více informací
                </button>
              </div>
              <button className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-4 rounded-2xl shadow-lg shadow-emerald-400/25 transition-all duration-300" onClick={() => { const el = document.querySelector('#webinar'); if(el) el.scrollIntoView({behavior:'smooth'}); }}>
                Bezplatná živá ukázka
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
